// FE-04: the sign-in screen loads only what it needs. App, and with it the
// rest of the application, is code split and loads only when it is needed:
// - someone is signed in (a stored session, or a sign-in on this screen or in
//   another tab);
// - the address carries an invitation or password-reset link, or an
//   authentication error (App runs that ceremony, so the gate never touches
//   the address or the session first);
// - the session cannot be read (App reads it again and reports why).
// Otherwise this screen draws the sign-in form itself, with the theme App
// would use for nobody signed in. Once App has loaded it stays: App shows its
// own sign-in screen after a sign-out, with the reason the session ended.
import { Suspense, useEffect, useState } from 'react';
import AppGlobalStyles from './AppGlobalStyles.js';
import AuthScreen from './AuthScreen.js';
import { normalizeThemePreference, storedThemeKey, systemThemePreference, themeFromConfig } from './appTheme.js';
import { authCallbackDetails } from './authSecurity.js';
import { clearSensitiveBrowserData, readStoredItem, removeExpiredSensitiveBrowserData } from './browserStorage.js';
import { DEFAULT_CONFIG } from './defaultConfig.js';
import './SignInGate.css';

// The reason the last session ended, kept for the next sign-in screen
// (useApplicationAccess.js writes it, and removes it once access is granted).
const SESSION_END_MESSAGE_KEY = 'riskTool_sessionEndMessage';

// 'app' when App must start now, else 'checking' until the session is read.
export function initialEntry(location = globalThis.location) {
  return authCallbackDetails(location).hasCallback ? 'app' : 'checking';
}

// The theme App draws with nobody signed in: the browser's anonymous
// preference, else the default configuration's theme; 'system' follows the
// device.
export function signedOutTheme(systemTheme = systemThemePreference()) {
  const preference = normalizeThemePreference(readStoredItem('localStorage', storedThemeKey(null)))
    || themeFromConfig(DEFAULT_CONFIG)
    || 'system';
  return preference === 'system' ? systemTheme : preference;
}

// The loading state App shows first ("Loading account access"), drawn with
// this screen's own styles while the session is read or App's code loads.
function EntryLoading() {
  return <div className="sign-in-gate__loading" role="status" aria-live="polite" aria-busy="true">
    <span className="sign-in-gate__label">Loading account access</span>
    {[0, 1, 2, 3, 4].map(index => <span key={index} className="sign-in-gate__bar" aria-hidden="true"/>)}
  </div>;
}

export default function SignInGate({ supabase, App, preloadApp }) {
  const [entry, setEntry] = useState(() => initialEntry());
  const [systemTheme, setSystemTheme] = useState(systemThemePreference);
  const appOpen = entry === 'app';

  useEffect(() => {
    if (appOpen || typeof window === 'undefined' || !window.matchMedia) return undefined;
    const media = window.matchMedia('(prefers-color-scheme: light)');
    const sync = () => setSystemTheme(media.matches ? 'light' : 'dark');
    sync();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', sync);
      return () => media.removeEventListener('change', sync);
    }
    media.addListener?.(sync);
    return () => media.removeListener?.(sync);
  }, [appOpen]);

  useEffect(() => {
    if (appOpen) return undefined;
    let active = true;
    const openApp = () => { if (active) setEntry('app'); };
    // A session from any event (a sign-in here or in another tab, the stored
    // session) starts App. A sign-out elsewhere removes the drafts and caches
    // that hold player, case or review content, as App does (FE-07).
    const { data:{ subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) openApp();
      else if (event === 'SIGNED_OUT') clearSensitiveBrowserData();
    });
    // As App does when it opens: drafts older than their maximum age go, and
    // with nobody signed in every sensitive draft goes (FE-07).
    removeExpiredSensitiveBrowserData();
    Promise.resolve().then(() => supabase.auth.getSession()).then(result => {
      if (!active) return;
      if (result?.error || result?.data?.session) { openApp(); return; }
      clearSensitiveBrowserData();
      setEntry(current => (current === 'checking' ? 'signed-out' : current));
    }, openApp);
    return () => { active = false; subscription?.unsubscribe(); };
  }, [appOpen, supabase]);

  const theme = signedOutTheme(systemTheme);
  const styles = <AppGlobalStyles branding={DEFAULT_CONFIG.branding} theme={theme}/>;
  if (appOpen) return <Suspense fallback={<>{styles}<EntryLoading/></>}><App/></Suspense>;
  return <>
    {styles}
    {entry === 'checking'
      ? <EntryLoading/>
      : <AuthScreen sessionMessage={readStoredItem('sessionStorage', SESSION_END_MESSAGE_KEY) || ''} onSignIn={preloadApp}/>}
  </>;
}
